import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  AlertCircle,
  AlignLeft,
  CalendarRange,
  CalendarCheck2,
  Cake,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Filter,
  Link as LinkIcon,
  ListChecks,
  Loader2,
  MapPin,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserCheck,
  Users,
  X,
  XCircle,
} from "lucide-react";
import api from "../../Services/api";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useAuthContext } from "../../context/authContext";
import { useOperador } from "../../Hooks/useOperador";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./index.css";

const STATUS = [
  { value: "backlog", label: "Atividade prevista", tone: "neutral" },
  { value: "afazer", label: "A Fazer", tone: "blue" },
  { value: "andamento", label: "Em Andamento", tone: "warning" },
  { value: "revisao", label: "Revisão", tone: "review" },
  { value: "concluido", label: "Concluido", tone: "success" },
  { value: "cancelado", label: "Cancelado", tone: "danger" },
];

const BOARD_COLUMNS = STATUS;

const PRIORIDADES = [
  { value: "baixa", label: "Baixa" },
  { value: "media", label: "Media" },
  { value: "alta", label: "Alta" },
];

const TIPOS = [
  { value: "tarefa", label: "Tarefa" },
  { value: "reuniao", label: "Reuniao" },
  { value: "lembrete", label: "Lembrete" },
  { value: "outro", label: "Outro" },
];

const HISTORICO_ACAO_LABELS = {
  criou: "Criou",
  alterou: "Alterou",
  mudou_status: "Mudou status",
  finalizou: "Finalizou",
  cancelou: "Cancelou",
  adicionou_participante: "Adicionou participante",
  removeu_participante: "Removeu participante",
  aprovou: "Aprovou",
  recusou: "Recusou",
};

const initialFilters = {
  scope: "grupo",
  search: "",
  cliente: "",
  status: "",
  prioridade: "",
  operador: "",
  inicio: "",
  fim: "",
};

const emptyForm = {
  titulo: "",
  descricao: "",
  descricaoNova: "",
  tipo: "tarefa",
  IDCliente: "",
  IDProcesso: "",
  IDAgendaGrupo: "",
  IDOperadorResponsavel: "",
  participantes: [],
  dataInicio: "",
  dataFim: "",
  prioridade: "media",
  local: "",
  linkReuniao: "",
  statusKanban: "backlog",
};

const ALL_CLIENTS_VALUE = "__todos__";

const removeAccents = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const normalizeStatus = (value) => {
  const raw = removeAccents(value || "backlog")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (["AFAZER", "A_FAZER", "TODO"].includes(raw)) return "afazer";
  if (["EMANDAMENTO", "EM_ANDAMENTO", "ANDAMENTO", "DOING"].includes(raw)) return "andamento";
  if (["REVISAO", "EM_REVISAO", "REVIEW"].includes(raw)) return "revisao";
  if (["CONCLUIDO", "CONCLUIDA", "DONE"].includes(raw)) return "concluido";
  if (["CANCELADO", "CANCELADA"].includes(raw)) return "cancelado";
  if (["BACKLOG"].includes(raw)) return "backlog";
  return STATUS.some((s) => s.value === String(value).toLowerCase()) ? String(value).toLowerCase() : "backlog";
};

const normalizeApproval = (value) => {
  const raw = removeAccents(value || "APROVADO").toUpperCase();
  if (["PENDENTE", "AGUARDANDO", "CONVITE_PENDENTE"].includes(raw)) return "pendente";
  if (["RECUSADO", "REJEITADO"].includes(raw)) return "recusado";
  if (["CANCELADO", "CANCELADA"].includes(raw)) return "cancelado";
  if (["CONCLUIDO", "CONCLUIDA"].includes(raw)) return "concluido";
  return "aprovado";
};

const normalizeTipo = (value) => {
  const raw = removeAccents(value || "tarefa")
    .toLowerCase()
    .replace(/\s+/g, "");

  if (["reuniao", "reuniaoonline", "meeting"].includes(raw)) return "reuniao";
  if (["lembrete", "prazo", "followup", "follow-up"].includes(raw)) return "lembrete";
  if (["outro", "compromisso"].includes(raw)) return "outro";
  return "tarefa";
};

const getId = (item) => item?.IDAgendaTarefa || item?.IDAgenda || item?.id || item?.ID;
const getOperatorId = (item, keys) => keys.map((key) => item?.[key]).find(Boolean);
const getOperatorName = (operadoresMap, id, fallback) => operadoresMap.get(Number(id))?.nome || fallback || (id ? `Operador ${id}` : "Sem responsavel");

const parseList = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.agenda)) return data.agenda;
  if (Array.isArray(data?.tarefas)) return data.tarefas;
  if (Array.isArray(data?.Clientes)) return data.Clientes;
  if (Array.isArray(data?.Processos)) return data.Processos;
  return [];
};

const toDatetimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const formatDate = (value) => {
  if (!value) return "Sem data";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Data invalida";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDescriptionEntryDate = () =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());

const appendDescriptionEntry = (currentDescription, newMessage, operatorName) => {
  const message = String(newMessage || "").trim();
  if (!message) return String(currentDescription || "").trim();

  const header = `(${formatDescriptionEntryDate()} - ${operatorName || "Operador"})`;
  return [String(currentDescription || "").trim(), `${header} ${message}`]
    .filter(Boolean)
    .join("\n");
};

const formatCardDay = (value) => {
  if (!value) return { day: "--", month: "---", weekday: "Sem data" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { day: "--", month: "---", weekday: "Data invalida" };

  return {
    day: new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(date),
    month: new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(date).replace(".", "").toUpperCase(),
    weekday: new Intl.DateTimeFormat("pt-BR", { weekday: "short" }).format(date).replace(".", ""),
  };
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const isSameDay = (first, second) => {
  if (!first || !second) return false;
  const firstDate = new Date(first);
  const secondDate = new Date(second);
  if (Number.isNaN(firstDate.getTime()) || Number.isNaN(secondDate.getTime())) return false;
  return firstDate.toDateString() === secondDate.toDateString();
};

const startOfCalendar = (date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());
  start.setHours(0, 0, 0, 0);
  return start;
};

const calendarDays = (date) => {
  const start = startOfCalendar(date);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
};

const monthLabel = (date) =>
  new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);

const statusMeta = (value) => STATUS.find((s) => s.value === normalizeStatus(value)) || STATUS[0];
const typeLabel = (value) => TIPOS.find((tipo) => tipo.value === normalizeTipo(value))?.label || "Tarefa";
const operatorLabel = (operator) => operator?.nome || operator?.Nome || operator?.nomeOperador || `Operador ${operator?.IDOperador || ""}`;
const getClientId = (value) => value?.IDCliente || value?.idCliente || value?.Cliente?.IDCliente || value?.cliente?.IDCliente;
const clientLabel = (client) =>
  client?.nome || client?.Nome || client?.nomeCliente || client?.clienteNome || client?.razaoSocial || client?.RazaoSocial || `Cliente ${getClientId(client) || ""}`;
const processLabel = (process) => process?.descricao || process?.Descricao || process?.nome || process?.Nome || `Processo ${process?.IDProcesso || ""}`;
const itemClientLabel = (item) =>
  item?.clienteNome || item?.nomeCliente || item?.Cliente?.nome || item?.Cliente?.Nome || (item?.IDCliente ? `Cliente ${item.IDCliente}` : "");
const itemMunicipio = (item) =>
  item?.clienteMunicipio || item?.municipioCliente || item?.Cliente?.municipio || item?.cliente?.municipio || "";
const itemProcessLabel = (item) =>
  item?.processoNome || item?.descricaoProcesso || item?.Processo?.descricao || item?.Processo?.Descricao || item?.processoDescricao || (item?.IDProcesso ? `Processo ${item.IDProcesso}` : "");
const prazoDateValue = (item) => new Date(item?.dataFim || item?.dataInicio || 0).getTime();
const compareBoardItemsByDate = (a, b) => {
  const prazoDifference = prazoDateValue(a) - prazoDateValue(b);
  if (prazoDifference !== 0) return prazoDifference;

  const orderDifference = Number(a.ordemKanban || 0) - Number(b.ordemKanban || 0);
  if (orderDifference !== 0) return orderDifference;

  return Number(getId(b) || 0) - Number(getId(a) || 0);
};
const isPendingForCurrentOperator = (item) =>
  item?.convitePendenteParaMim === true || normalizeApproval(item?.statusParticipanteOperador) === "pendente";
const isRejectedForCurrentOperator = (item) =>
  normalizeApproval(item?.statusParticipanteOperador) === "recusado";
const isAcceptedForCurrentOperator = (item) =>
  item?.vinculadoAoOperadorAtual === true || normalizeApproval(item?.statusParticipanteOperador) === "aprovado";
const canManageAgendaItem = (item, isAdmin, currentOperatorId) =>
  isAdmin ||
  Number(item?.IDOperadorCriador) === Number(currentOperatorId) ||
  Number(item?.IDOperadorResponsavel) === Number(currentOperatorId);
const canFullyEditAgendaItem = (item, isAdmin, isManager, currentOperatorId) =>
  isManager || canManageAgendaItem(item, isAdmin, currentOperatorId);
const canEditAcceptedTaskLimited = (item, isAdmin, isManager, currentOperatorId) =>
  !canFullyEditAgendaItem(item, isAdmin, isManager, currentOperatorId) &&
  normalizeTipo(item?.tipo) === "tarefa" &&
  isAcceptedForCurrentOperator(item) &&
  !isPendingForCurrentOperator(item) &&
  !isRejectedForCurrentOperator(item);

const getDateTone = (item) => {
  if (!item.dataInicio || ["concluido", "cancelado"].includes(normalizeStatus(item.statusKanban))) return "default";

  const reference = new Date(item.dataFim || item.dataInicio);
  if (Number.isNaN(reference.getTime())) return "default";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const itemDay = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());

  if (itemDay < today) return "overdue";
  if (itemDay.getTime() === today.getTime()) return "today";
  return "default";
};

const confirmAction = async ({ title, text, confirmButtonText = "Confirmar", icon = "question" }) => {
  const result = await Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Voltar",
    reverseButtons: true,
  });
  return result.isConfirmed;
};

function AgendaFilters({ filters, setFilters, isAdmin, isRealAdmin, operadores, clientes, view, setView, onNew, onRefresh, loading, showAllAgenda, setShowAllAgenda }) {
  const update = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  return (
    <section className="agenda-toolbar" aria-label="Filtros da agenda">
      <div className="agenda-view-switch" role="tablist" aria-label="Visualizacao">
        <button type="button" className={view === "kanban" ? "active" : ""} onClick={() => setView("kanban")} aria-pressed={view === "kanban"}>
          <ListChecks size={18} /> Quadro
        </button>
        <button type="button" className={view === "lista" ? "active" : ""} onClick={() => setView("lista")} aria-pressed={view === "lista"}>
          <CalendarCheck2 size={18} /> Agenda
        </button>
        <button type="button" className={view === "aniversarios" ? "active" : ""} onClick={() => setView("aniversarios")} aria-pressed={view === "aniversarios"}>
          <Cake size={18} /> Aniversarios
        </button>
      </div>

      <label className="agenda-search">
        <Search size={18} aria-hidden="true" />
        <input
          value={filters.search}
          onChange={(e) => update("search", e.target.value)}
          placeholder={view === "aniversarios" ? "Buscar colaborador ou grupo" : "Buscar por titulo, local ou responsavel"}
        />
      </label>

      {view !== "aniversarios" && <div className="agenda-filter-grid">
        <label>
          <span><Filter size={15} /> Escopo</span>
          <select value={filters.scope} onChange={(e) => update("scope", e.target.value)}>
            <option value="grupo">Meu grupo</option>
            <option value="meus">Meus itens</option>
            <option value="atribuidos">Atribuidos a mim</option>
            {isAdmin && <option value="todos">Todos</option>}
          </select>
        </label>

        <label>
          <span><Users size={15} /> Operador</span>
          <select value={filters.operador} onChange={(e) => update("operador", e.target.value)}>
            <option value="">Todos</option>
            {operadores.map((op) => (
              <option key={op.IDOperador} value={op.IDOperador}>{op.nome}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Cliente</span>
          <select value={filters.cliente} onChange={(e) => update("cliente", e.target.value)}>
            <option value="">Todos</option>
            {clientes.filter(getClientId).map((client) => (
              <option key={getClientId(client)} value={getClientId(client)}>{clientLabel(client)}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select value={filters.status} onChange={(e) => update("status", e.target.value)}>
            <option value="">Todos</option>
            {STATUS.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Prioridade</span>
          <select value={filters.prioridade} onChange={(e) => update("prioridade", e.target.value)}>
            <option value="">Todas</option>
            {PRIORIDADES.map((prioridade) => (
              <option key={prioridade.value} value={prioridade.value}>{prioridade.label}</option>
            ))}
          </select>
        </label>

        <div className="agenda-date-filter-row">
          <label>
            <span>Inicio</span>
            <input type="date" value={filters.inicio} onChange={(e) => update("inicio", e.target.value)} />
          </label>

          <label>
            <span>Fim</span>
            <input type="date" value={filters.fim} onChange={(e) => update("fim", e.target.value)} />
          </label>
        </div>
      </div>}

      <div className="agenda-toolbar-actions">
        {view !== "aniversarios" && isRealAdmin && (
          <label className="agenda-view-all-check">
            <input
              type="checkbox"
              checked={showAllAgenda}
              onChange={(event) => setShowAllAgenda(event.target.checked)}
            />
            Visualizar tudo
          </label>
        )}
        <button type="button" className="agenda-icon-button" onClick={onRefresh} aria-label="Atualizar agenda" title="Atualizar agenda" disabled={loading}>
          <RefreshCw size={18} className={loading ? "spin" : ""} /> Atualizar
        </button>
        {view !== "aniversarios" && (
          <button type="button" className="agenda-primary-button" onClick={onNew}>
            <Plus size={18} /> Novo item
          </button>
        )}
      </div>
    </section>
  );
}

function AgendaCard({ item, operadoresMap, onEdit, onMove, onCancel, onDelete, onConclude, onOpenDetails, onDragStart, onDragEnd, onDragOver, onDrop, isDropTarget, onCanChangeStatus, onCanManageItem, onCanBypassInvitationStatus }) {
  const meta = statusMeta(item.statusKanban);
  const isRejected = item.statusParticipanteOperador === "recusado";
  const isPending = isPendingForCurrentOperator(item);
  const canBypassInvitationStatus = onCanBypassInvitationStatus
    ? onCanBypassInvitationStatus(item)
    : false;
  const isReadonly = (isRejected || isPending) && !canBypassInvitationStatus;
  const canChangeStatus = onCanChangeStatus ? onCanChangeStatus(item) : !isReadonly;
  const canManageItem = onCanManageItem ? onCanManageItem(item) : true;
  const responsavel = getOperatorName(operadoresMap, item.IDOperadorResponsavel, item.responsavelNome || item.responsavel);
  const dateTone = getDateTone(item);
  const cardDay = formatCardDay(item.dataInicio);
  const startTime = formatTime(item.dataInicio);
  const endTime = formatTime(item.dataFim);
  const initial = String(responsavel || "?").trim().charAt(0).toUpperCase();
  const cliente = itemClientLabel(item);
  const processo = itemProcessLabel(item);

  return (
    <article
      className={`agenda-card agenda-card--${meta.tone} agenda-card--date-${dateTone} ${isDropTarget ? "agenda-card--drop-target" : ""}`}
      draggable={!isReadonly && canChangeStatus}
      onDragStart={(event) => onDragStart(event, item)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={(event) => {
        if (!event.target.closest("button, summary, details, a")) onOpenDetails(item);
      }}
      onKeyDown={(event) => {
        if (event.target.closest("button, summary, details, a")) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenDetails(item);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Ver detalhes de ${item.titulo}`}
    >
      <header className="agenda-card-header">
        <span className="agenda-card-type">{typeLabel(item.tipo)}</span>
        <span className={`agenda-priority priority-${String(item.prioridade || "media").toLowerCase()}`}>{item.prioridade || "media"}</span>
      </header>

      <h3 title={item.titulo}>{item.titulo}</h3>

      <div className={`agenda-card-schedule schedule-${dateTone}`}>
        <div className="agenda-card-date-tile" aria-hidden="true">
          <strong>{cardDay.day}</strong>
          <span>{cardDay.month}</span>
        </div>
        <div className="agenda-card-date-copy">
          <div>
            <strong>{cardDay.weekday}</strong>
            {dateTone !== "default" && (
              <span className={`agenda-card-date-alert alert-${dateTone}`}>
                {dateTone === "today" ? "Hoje" : "Atrasado"}
              </span>
            )}
          </div>
          <span>
            {startTime || "Horario nao definido"}
            {endTime && isSameDay(item.dataInicio, item.dataFim) ? ` - ${endTime}` : ""}
          </span>
          {item.dataFim && !isSameDay(item.dataInicio, item.dataFim) && (
            <small>Termina em {formatDate(item.dataFim)}</small>
          )}
        </div>
      </div>

      <div className="agenda-card-context">
        <div className="agenda-card-owner" title={responsavel}>
          <span className="agenda-card-avatar" aria-hidden="true">{initial}</span>
          <span>{responsavel}</span>
        </div>
        {(cliente || processo) && (
          <span className="agenda-card-scope" title={[cliente, processo].filter(Boolean).join(" - ")}>
            {cliente}{cliente && processo ? " - " : ""}{processo}
          </span>
        )}
        {item.local && (
          <span className="agenda-card-location" title={item.local}>
            <MapPin size={14} aria-hidden="true" /> {item.local}
          </span>
        )}
        {item.agendaGrupoNome && (
          <span className="agenda-card-location" title={item.agendaGrupoNome}>
            <Users size={14} aria-hidden="true" /> {item.agendaGrupoNome}
          </span>
        )}
      </div>

      {item.statusParticipanteOperador === "recusado" && (
        <div className="agenda-card-response agenda-card-response--rejected">
          <XCircle size={15} aria-hidden="true" />
          <span>Recusado por voce</span>
        </div>
      )}

      {isReadonly ? (
        <footer className="agenda-card-footer agenda-card-footer--readonly">
          <button type="button" className="agenda-card-details-button" onClick={() => onOpenDetails(item)}>
            Ver detalhes <ChevronRight size={16} />
          </button>
        </footer>
      ) : (
        <footer className="agenda-card-footer">
          <button type="button" className="agenda-card-move" onClick={() => onMove(item)} disabled={!canChangeStatus}>
            Mover <ChevronRight size={16} />
          </button>
          <div className="agenda-card-actions">
            {meta.value !== "concluido" && meta.value !== "cancelado" && (
              <button type="button" onClick={() => onConclude(item)} aria-label="Concluir item" title="Concluir" disabled={!canManageItem}><CheckCircle2 size={16} /></button>
            )}
            <button type="button" onClick={() => onEdit(item)} aria-label="Editar item" title="Editar"><Edit3 size={16} /></button>
            {meta.value !== "cancelado" && (
              <details className="agenda-card-more">
                <summary aria-label="Mais ações" title="Mais ações"><MoreHorizontal size={18} /></summary>
                <div>
                  <button type="button" onClick={() => onCancel(item)} disabled={!canManageItem}>
                    <XCircle size={16} /> Cancelar
                  </button>
                  {canManageItem && onDelete && (
                    <button type="button" onClick={() => onDelete(item)}>
                      <Trash2 size={16} /> Excluir
                    </button>
                  )}
                </div>
              </details>
            )}
          </div>
        </footer>
      )}
    </article>
  );
}

function AgendaDetailModal({ item, loading, operadoresMap, isAdmin, canBypassInvitationStatus, onClose, onEdit }) {
  const [showHistorico, setShowHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoError, setHistoricoError] = useState("");
  const itemId = item ? getId(item) : null;
  const meta = statusMeta(item?.statusKanban);
  const isPending = isPendingForCurrentOperator(item);
  const responsavel = item
    ? getOperatorName(operadoresMap, item.IDOperadorResponsavel, item.responsavelNome || item.responsavel)
    : "";
  const participantes = item?.participanteDetalhes || [];

  const loadHistorico = useCallback(async () => {
    if (!itemId) return;

    setHistoricoLoading(true);
    setHistoricoError("");

    try {
      const response = await api.get(`/agenda/${itemId}/historico`);
      setHistorico(parseList(response));
    } catch (err) {
      console.error(err);
      setHistoricoError("Nao foi possivel carregar o historico.");
    } finally {
      setHistoricoLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    setShowHistorico(false);
    setHistorico([]);
    setHistoricoError("");
  }, [itemId]);

  const toggleHistorico = async () => {
    const nextVisible = !showHistorico;
    setShowHistorico(nextVisible);

    if (nextVisible && historico.length === 0 && !historicoLoading) {
      await loadHistorico();
    }
  };

  if (!item && !loading) return null;

  return (
    <div className="agenda-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="agenda-detail-modal" role="dialog" aria-modal="true" aria-labelledby="agenda-detail-title">
        <header>
          <div>
            <span>Detalhes do item</span>
            <h2 id="agenda-detail-title">{item?.titulo || "Carregando..."}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar detalhes" title="Fechar">
            <X size={20} />
          </button>
        </header>

        {loading ? (
          <div className="agenda-detail-loading">
            <Loader2 size={26} className="spin" />
            <span>Carregando detalhes...</span>
          </div>
        ) : (
          <div className="agenda-detail-content">
            <div className="agenda-detail-badges">
              <span className={`agenda-list-status list-status-${meta.tone}`}>{meta.label}</span>
              <span className={`agenda-priority priority-${item.prioridade || "media"}`}>{item.prioridade || "media"}</span>
              <span className={`agenda-approval approval-${item.statusAgenda || "aprovado"}`}>{item.statusAgenda || "aprovado"}</span>
            </div>

            <div className="agenda-detail-grid">
              <div>
                <CalendarRange size={18} />
                <span>Inicio</span>
                <strong>{formatDate(item.dataInicio)}</strong>
              </div>
              <div>
                <CalendarRange size={18} />
                <span>Fim</span>
                <strong>{item.dataFim ? formatDate(item.dataFim) : "Sem data definida"}</strong>
              </div>
              <div>
                <UserCheck size={18} />
                <span>Responsavel</span>
                <strong>{responsavel}</strong>
              </div>
              {(itemClientLabel(item) || itemProcessLabel(item)) && (
                <div>
                  <Users size={18} />
                  <span>Cliente / Processo</span>
                  <strong>{[itemClientLabel(item), itemProcessLabel(item)].filter(Boolean).join(" - ")}</strong>
                </div>
              )}
              <div>
                <ListChecks size={18} />
                <span>Tipo</span>
                <strong>{typeLabel(item.tipo)}</strong>
              </div>
              {item.agendaGrupoNome && (
                <div>
                  <Users size={18} />
                  <span>Grupo</span>
                  <strong>{item.agendaGrupoNome}</strong>
                </div>
              )}
              {item.local && (
                <div>
                  <MapPin size={18} />
                  <span>Local</span>
                  <strong>{item.local}</strong>
                </div>
              )}
              {item.linkReuniao && (
                <div>
                  <LinkIcon size={18} />
                  <span>Reuniao</span>
                  <a href={item.linkReuniao} target="_blank" rel="noreferrer">Abrir link</a>
                </div>
              )}
            </div>

            <section className="agenda-detail-description">
              <h3><AlignLeft size={17} /> Descricao</h3>
              <p>{item.descricao || "Nenhuma descricao informada."}</p>
            </section>

            <section className="agenda-detail-participants">
              <h3><Users size={17} /> Participantes <span>{participantes.length}</span></h3>
              <div>
                {participantes.map((participante) => {
                  const nomeParticipante = getOperatorName(operadoresMap, participante.IDOperador);
                  return (
                    <article key={participante.IDAgendaTarefaParticipante || participante.IDOperador}>
                      <span className="agenda-card-avatar">{nomeParticipante.charAt(0).toUpperCase()}</span>
                      <div>
                        <strong>{nomeParticipante}</strong>
                        <small className={`approval-${normalizeApproval(participante.statusParticipante)}`}>
                          {normalizeApproval(participante.statusParticipante)}
                        </small>
                        <small className={participante.dataVisualizacao ? "agenda-viewed" : "agenda-not-viewed"}>
                          {participante.dataVisualizacao
                            ? `Visualizado em ${formatDate(participante.dataVisualizacao)}`
                            : "Nao visualizado"}
                        </small>
                      </div>
                    </article>
                  );
                })}
                {participantes.length === 0 && <p>Nenhum participante adicional.</p>}
              </div>
            </section>

            {isAdmin && showHistorico && (
              <section className="agenda-detail-history">
                <h3><ListChecks size={17} /> Historico <span>{historico.length}</span></h3>
                {historicoLoading && (
                  <div className="agenda-detail-history-state">
                    <Loader2 size={20} className="spin" />
                    <span>Carregando historico...</span>
                  </div>
                )}
                {!historicoLoading && historicoError && (
                  <div className="agenda-detail-history-state agenda-detail-history-error">
                    <AlertCircle size={20} />
                    <span>{historicoError}</span>
                    <button type="button" onClick={loadHistorico}>Tentar novamente</button>
                  </div>
                )}
                {!historicoLoading && !historicoError && historico.length > 0 && (
                  <div className="agenda-history-list">
                    {historico.map((registro) => {
                      const nomeOperador = getOperatorName(operadoresMap, registro.IDOperador);
                      const acao = HISTORICO_ACAO_LABELS[registro.acao] || registro.acao || "Acao registrada";
                      return (
                        <article key={registro.IDAgendaTarefaHistorico}>
                          <span className="agenda-history-dot" aria-hidden="true" />
                          <div>
                            <strong>{acao}</strong>
                            <small>{nomeOperador} - {formatDate(registro.dataCadastro)}</small>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
                {!historicoLoading && !historicoError && historico.length === 0 && (
                  <p>Nenhum historico registrado.</p>
                )}
              </section>
            )}
          </div>
        )}

        {!loading && item && (
          <footer>
            {isAdmin && (
              <button type="button" className="agenda-secondary-button" onClick={toggleHistorico} disabled={historicoLoading}>
                {historicoLoading ? <Loader2 size={17} className="spin" /> : <ListChecks size={17} />}
                {showHistorico ? "Ocultar historico" : "Exibir historico"}
              </button>
            )}
            <button type="button" className="agenda-secondary-button" onClick={onClose}>Fechar</button>
            <button type="button" className="agenda-primary-button" onClick={() => onEdit(item)} disabled={isPending && !canBypassInvitationStatus}>
              <Edit3 size={17} /> Editar
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}

function AgendaColumn({ status, items, children, isDragOver, onDragOver, onDragLeave, onDrop }) {
  return (
    <section
      className={`agenda-column ${isDragOver ? "agenda-column--drag-over" : ""}`}
      aria-label={status.label}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <header>
        <span className={`agenda-column-dot dot-${status.tone}`} />
        <h2>{status.label}</h2>
        <strong aria-label={`${items.length} itens`}>{items.length}</strong>
      </header>
      <div className="agenda-column-list">
        {items.length > 0 ? children : <p className="agenda-column-empty">Sem itens aqui.</p>}
      </div>
    </section>
  );
}

function AgendaBoard({ items, operadoresMap, actions }) {
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState("");
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const handleDragStart = (event, item) => {
    if (
      event.target.closest("button, summary, details") ||
      isPendingForCurrentOperator(item) ||
      (actions.onCanChangeStatus && !actions.onCanChangeStatus(item))
    ) {
      event.preventDefault();
      return;
    }

    const itemId = getId(item);
    setDraggedItemId(itemId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(itemId));
  };

  const handleDrop = (event, status, targetItemId = null) => {
    event.preventDefault();
    event.stopPropagation();
    const itemId = draggedItemId || Number(event.dataTransfer.getData("text/plain"));
    const item = items.find((agendaItem) => Number(getId(agendaItem)) === Number(itemId));
    if (
      !item ||
      status.virtual ||
      isPendingForCurrentOperator(item) ||
      (actions.onCanChangeStatus && !actions.onCanChangeStatus(item))
    ) {
      setDraggedItemId(null);
      setDragOverStatus("");
      setDragOverItemId(null);
      return;
    }

    const destinationItems = items
      .filter((agendaItem) => {
        const readonly = agendaItem.statusParticipanteOperador === "recusado" || isPendingForCurrentOperator(agendaItem);
        return !readonly && normalizeStatus(agendaItem.statusKanban) === status.value;
      })
      .sort(compareBoardItemsByDate)
      .filter((agendaItem) => Number(getId(agendaItem)) !== Number(itemId));

    const targetIndex = targetItemId
      ? destinationItems.findIndex(
          (agendaItem) => Number(getId(agendaItem)) === Number(targetItemId),
        )
      : -1;
    const insertIndex = targetIndex >= 0 ? targetIndex : destinationItems.length;
    destinationItems.splice(insertIndex, 0, item);
    const orderedIds = destinationItems.map(getId);

    setDraggedItemId(null);
    setDragOverStatus("");
    setDragOverItemId(null);

    actions.onReorder(item, status.value, orderedIds);
  };

  return (
    <div className={`agenda-board ${draggedItemId ? "agenda-board--dragging" : ""}`}>
      {BOARD_COLUMNS.map((status) => {
        const columnItems = items.filter((item) => {
          const rejected = item.statusParticipanteOperador === "recusado";
          if (status.value === "recusado") return rejected;
          return !rejected && normalizeStatus(item.statusKanban) === status.value;
        }).sort(compareBoardItemsByDate);
        return (
          <AgendaColumn
            key={status.value}
            status={status}
            items={columnItems}
            isDragOver={dragOverStatus === status.value}
            onDragOver={(event) => {
              if (status.virtual) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDragOverStatus(status.value);
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setDragOverStatus("");
            }}
            onDrop={(event) => {
              if (!status.virtual) handleDrop(event, status);
            }}
          >
            {columnItems.map((item) => (
              <AgendaCard
                key={getId(item)}
                item={item}
                operadoresMap={operadoresMap}
                {...actions}
                onDragStart={handleDragStart}
                isDropTarget={
                  Number(dragOverItemId) === Number(getId(item)) &&
                  Number(draggedItemId) !== Number(getId(item))
                }
                onDragOver={(event) => {
                  if (status.virtual || isPendingForCurrentOperator(item) || Number(draggedItemId) === Number(getId(item))) return;
                  event.preventDefault();
                  event.stopPropagation();
                  event.dataTransfer.dropEffect = "move";
                  setDragOverStatus(status.value);
                  setDragOverItemId(getId(item));
                }}
                onDrop={(event) => {
                  if (!status.virtual) handleDrop(event, status, getId(item));
                }}
                onDragEnd={() => {
                  setDraggedItemId(null);
                  setDragOverStatus("");
                  setDragOverItemId(null);
                }}
              />
            ))}
          </AgendaColumn>
        );
      })}
    </div>
  );
}

function AgendaDayModal({ day, items, operadoresMap, onClose, onOpenDetails }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const dayLabel = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(day);

  return (
    <div className="agenda-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="agenda-detail-modal agenda-day-modal" role="dialog" aria-modal="true" aria-labelledby="agenda-day-modal-title">
        <header>
          <div>
            <span>{items.length} {items.length === 1 ? "compromisso" : "compromissos"}</span>
            <h2 id="agenda-day-modal-title">{dayLabel}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar compromissos do dia" title="Fechar">
            <X size={20} />
          </button>
        </header>

        <div className="agenda-day-list">
          {items.map((item) => {
            const meta = statusMeta(item.statusKanban);
            const responsavel = getOperatorName(
              operadoresMap,
              item.IDOperadorResponsavel,
              item.responsavelNome || item.responsavel,
            );
            const scopeLabel = [itemClientLabel(item), itemProcessLabel(item)].filter(Boolean).join(" - ");

            return (
              <button
                type="button"
                key={getId(item)}
                className={`agenda-day-item day-item-${meta.tone}`}
                onClick={() => {
                  onClose();
                  onOpenDetails(item);
                }}
              >
                <time>{formatTime(item.dataInicio)}</time>
                <div>
                  <strong>{item.titulo}</strong>
                  <small>{[scopeLabel, responsavel].filter(Boolean).join(" - ")}</small>
                </div>
                <span className={`agenda-list-status list-status-${meta.tone}`}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function AgendaList({ items, operadoresMap, actions, onMonthChange }) {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const days = useMemo(() => calendarDays(currentMonth), [currentMonth]);
  const today = new Date();

  const itemsByDay = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (!item.dataInicio) return;
      const date = new Date(item.dataInicio);
      if (Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const current = map.get(key) || [];
      current.push(item);
      current.sort((a, b) => new Date(a.dataInicio) - new Date(b.dataInicio));
      map.set(key, current);
    });
    return map;
  }, [items]);

  useEffect(() => {
    if (!onMonthChange || days.length === 0) return;

    onMonthChange({
      periodoInicio: formatCalendarPeriod(days[0]),
      periodoFim: formatCalendarPeriod(days[days.length - 1], true),
    });
  }, [days, onMonthChange]);

  const moveMonth = (amount) => {
    setSelectedDay(null);
    setCurrentMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const sortedItems = useMemo(
    () => items
      .filter((item) => {
        const date = new Date(item.dataInicio);
        return !Number.isNaN(date.getTime()) &&
          date.getFullYear() === currentMonth.getFullYear() &&
          date.getMonth() === currentMonth.getMonth();
      })
      .sort((a, b) => prazoDateValue(a) - prazoDateValue(b)),
    [currentMonth, items]
  );

  const selectedDayItems = selectedDay
    ? itemsByDay.get(`${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`) || []
    : [];

  return (
    <section className="agenda-calendar-view">
      <header className="agenda-calendar-toolbar">
        <div>
          <button type="button" className="agenda-calendar-today" onClick={() => {
            setSelectedDay(null);
            setCurrentMonth(new Date());
          }}>Hoje</button>
          <button type="button" onClick={() => moveMonth(-1)} aria-label="Mes anterior" title="Mes anterior"><ChevronLeft size={18} /></button>
          <button type="button" onClick={() => moveMonth(1)} aria-label="Proximo mes" title="Proximo mes"><ChevronRight size={18} /></button>
        </div>
        <h2>{monthLabel(currentMonth)}</h2>
      </header>

      <div className="agenda-calendar-desktop">
        <div className="agenda-calendar-weekdays" aria-hidden="true">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"].map((weekday) => <span key={weekday}>{weekday}</span>)}
        </div>
        <div className="agenda-calendar-grid">
          {days.map((day) => {
            const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
            const dayItems = itemsByDay.get(key) || [];
            const outsideMonth = day.getMonth() !== currentMonth.getMonth();
            const isToday = isSameDay(day, today);

            return (
              <section
                key={day.toISOString()}
                className={`agenda-calendar-day ${outsideMonth ? "outside-month" : ""} ${isToday ? "is-today" : ""}`}
                aria-label={new Intl.DateTimeFormat("pt-BR", { dateStyle: "full" }).format(day)}
              >
                <header>
                  <span>{day.getDate()}</span>
                </header>
                <div className="agenda-calendar-events">
                  {dayItems.slice(0, 3).map((item) => {
                    const cliente = itemClientLabel(item);
                    const processo = itemProcessLabel(item);
                    const scopeLabel = [cliente, processo].filter(Boolean).join("/");
                    const showScope = ["reuniao", "lembrete"].includes(normalizeTipo(item.tipo)) && scopeLabel;
                    const eventTitle = `${formatTime(item.dataInicio)} - ${item.titulo}${showScope ? ` - ${scopeLabel}` : ""}`;
                    const isTask = normalizeTipo(item.tipo) === "tarefa";

                    return (
                      <button
                        type="button"
                        key={getId(item)}
                        className={`agenda-calendar-event event-${statusMeta(item.statusKanban).tone} event-type-${normalizeTipo(item.tipo)} ${showScope ? "agenda-calendar-event--with-client" : ""}`}
                        onClick={() => actions.onOpenDetails(item)}
                        title={eventTitle}
                      >
                        <time>{formatTime(item.dataInicio)}</time>
                        {isTask && <ListChecks size={12} aria-hidden="true" />}
                        <span>{item.titulo}</span>
                        {showScope && <small>{scopeLabel}</small>}
                      </button>
                    );
                  })}
                  {dayItems.length > 3 && (
                    <button type="button" className="agenda-calendar-more" onClick={() => setSelectedDay(day)}>
                      +{dayItems.length - 3} itens
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <div className="agenda-calendar-mobile">
        {sortedItems.length === 0 && (
          <p className="agenda-calendar-empty">Nenhum compromisso neste mes.</p>
        )}
        {sortedItems.map((item) => {
        const meta = statusMeta(item.statusKanban);
        const isPending = isPendingForCurrentOperator(item);
        const canChangeStatus = actions.onCanChangeStatus ? actions.onCanChangeStatus(item) : !isPending;
        const canManageItem = actions.onCanManageItem ? actions.onCanManageItem(item) : true;
        const canBypassInvitationStatus = actions.onCanBypassInvitationStatus
          ? actions.onCanBypassInvitationStatus(item)
          : false;
        const responsavel = getOperatorName(operadoresMap, item.IDOperadorResponsavel, item.responsavelNome || item.responsavel);
        const scopeLabel = [itemClientLabel(item), itemProcessLabel(item)].filter(Boolean).join(" - ");
        return (
          <article key={getId(item)} className="agenda-list-row" onClick={() => actions.onOpenDetails(item)}>
            <div className="agenda-list-date">
              <strong>{formatDate(item.dataInicio)}</strong>
              <span>{item.dataFim ? `ate ${formatDate(item.dataFim)}` : "sem fim"}</span>
            </div>
            <div className="agenda-list-main">
              <h3>{item.titulo}</h3>
              <p>{item.tipo || "Tarefa"} - {responsavel}</p>
              {scopeLabel && <p>{scopeLabel}</p>}
            </div>
            <span className={`agenda-priority priority-${String(item.prioridade || "media").toLowerCase()}`}>{item.prioridade || "media"}</span>
            <span className={`agenda-list-status list-status-${meta.tone}`}>{meta.label}</span>
            <div className="agenda-list-actions">
              <button type="button" onClick={(event) => { event.stopPropagation(); actions.onEdit(item); }} aria-label="Editar" title="Editar" disabled={isPending && !canBypassInvitationStatus}><Edit3 size={16} /></button>
              <button type="button" onClick={(event) => { event.stopPropagation(); actions.onMove(item); }} aria-label="Mover status" title="Mover status" disabled={!canChangeStatus}><ChevronRight size={16} /></button>
              <button type="button" onClick={(event) => { event.stopPropagation(); actions.onConclude(item); }} aria-label="Concluir" title="Concluir" disabled={!canManageItem}><CheckCircle2 size={16} /></button>
              <button type="button" onClick={(event) => { event.stopPropagation(); actions.onCancel(item); }} aria-label="Cancelar" title="Cancelar" disabled={!canManageItem}><XCircle size={16} /></button>
              {actions.onDelete && (
                <button type="button" onClick={(event) => { event.stopPropagation(); actions.onDelete(item); }} aria-label="Excluir" title="Excluir" disabled={!canManageItem}><Trash2 size={16} /></button>
              )}
            </div>
          </article>
        );
      })}
      </div>

      {selectedDay && (
        <AgendaDayModal
          day={selectedDay}
          items={selectedDayItems}
          operadoresMap={operadoresMap}
          onClose={() => setSelectedDay(null)}
          onOpenDetails={actions.onOpenDetails}
        />
      )}
    </section>
  );
}

const getNextBirthday = (birthday) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let year = today.getFullYear(); year <= today.getFullYear() + 4; year += 1) {
    const date = new Date(year, Number(birthday.mes) - 1, Number(birthday.dia));
    const validDate =
      date.getMonth() === Number(birthday.mes) - 1 &&
      date.getDate() === Number(birthday.dia);

    if (validDate && date >= today) {
      return {
        date,
        days: Math.round((date.getTime() - today.getTime()) / 86400000),
      };
    }
  }

  return { date: null, days: Number.MAX_SAFE_INTEGER };
};

const formatCalendarPeriod = (date, endOfDay = false) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day} ${endOfDay ? "23:59:59" : "00:00:00"}`;
};

function BirthdayList({ items }) {
  return (
    <section className="agenda-birthday-view" aria-label="Aniversarios dos grupos">
      <header>
        <div>
          <span className="agenda-panel-kicker"><Cake size={17} /> Aniversarios</span>
          <h2>Proximos aniversariantes</h2>
        </div>
        <p>Colaboradores dos seus grupos de agenda com data de nascimento cadastrada.</p>
      </header>

      <div className="agenda-birthday-grid">
        {items.map((item) => {
          const dateLabel = new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "long",
          }).format(item.proximoAniversario);
          const timingLabel = item.diasRestantes === 0
            ? "Hoje"
            : item.diasRestantes === 1
              ? "Amanha"
              : `Em ${item.diasRestantes} dias`;

          return (
            <article
              key={item.IDOperador}
              className={`agenda-birthday-card ${item.diasRestantes === 0 ? "is-today" : ""}`}
            >
              <div className="agenda-birthday-date">
                <strong>{String(item.dia).padStart(2, "0")}</strong>
                <span>{new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(item.proximoAniversario).replace(".", "")}</span>
              </div>
              <div className="agenda-birthday-person">
                <span className="agenda-birthday-avatar">{String(item.nome || "?").trim().charAt(0).toUpperCase()}</span>
                <div>
                  <h3>{item.nome}</h3>
                  <p>{dateLabel}</p>
                </div>
              </div>
              <div className="agenda-birthday-groups">
                {(item.grupos || []).map((grupo) => (
                  <span key={grupo.IDAgendaGrupo}>{grupo.nome}</span>
                ))}
              </div>
              <strong className="agenda-birthday-timing">{timingLabel}</strong>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AgendaModal({ show, item, form, setForm, operadores, agendaGrupos, isAdmin, isManager, currentOperatorId, onClose, onSave, saving }) {
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantClients, setParticipantClients] = useState([]);
  const [operatorAccessProcesses, setOperatorAccessProcesses] = useState([]);
  const loadingParticipantFilter = false;
  const participantClientId = form.IDCliente;
  const participantProcessId = form.IDProcesso;
  const allClientsSelected = participantClientId === ALL_CLIENTS_VALUE;
  const selectedAgendaGrupo = agendaGrupos.find(
    (grupo) => Number(grupo.IDAgendaGrupo) === Number(form.IDAgendaGrupo),
  );
  const groupOperatorIds = new Set(
    (selectedAgendaGrupo?.operadores || []).map(Number).filter(Boolean),
  );
  const tiposDisponiveis = TIPOS;
  const canUseAdminEditOptions = isAdmin || (isManager && Boolean(item));
  const isLimitedTaskEdit = item && canEditAcceptedTaskLimited(item, isAdmin, isManager, currentOperatorId);
  const lockFullEditFields = Boolean(isLimitedTaskEdit);
  const useDescriptionAppendMode = Boolean(item && normalizeTipo(form.tipo) === "tarefa");
  const isCommonOperatorCreatingReminder = false;
  const isPersonalItem = !form.IDAgendaGrupo;
  const disableParticipantEditing = lockFullEditFields || (isPersonalItem && !isAdmin);
  const showClientProcessFields = true;
  const hasSelectedGroup = Boolean(form.IDAgendaGrupo && selectedAgendaGrupo);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    setOperatorAccessProcesses([]);
    const loadClients = async () => {
      if (!isAdmin && !currentOperatorId) {
        setParticipantClients([]);
        return;
      }

      try {
        const [clientsResponse, processesResponse] = await Promise.all([
          api.get("/clientes"),
          api.get("/processos", { params: { page: 1, limit: 9999 } }),
        ]);
        const clients = parseList(clientsResponse).filter(getClientId);
        const accessProcesses = parseList(processesResponse);

        if (!cancelled) {
          setOperatorAccessProcesses(accessProcesses);
          setParticipantClients(clients);
          if (clients.length === 1) {
            setForm((prev) => ({
              ...prev,
              IDCliente:
                prev.IDCliente ||
                (prev.IDAgendaGrupo ? String(getClientId(clients[0])) : ""),
            }));
          }
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setParticipantClients([]);
          setOperatorAccessProcesses([]);
        }
      }
    };

    loadClients();
    return () => {
      cancelled = true;
    };
  }, [show, isAdmin, currentOperatorId, setForm]);

  useEffect(() => {
    if (!show) return;
    if (allClientsSelected) {
      setForm((prev) => ({ ...prev, IDProcesso: "" }));
      return;
    }

    const processes = operatorAccessProcesses.filter(
      (process) =>
        (!participantClientId ||
          Number(getClientId(process)) === Number(participantClientId)) &&
        Number(process.situacao) === 1,
    );
    if (participantClientId && processes.length === 1) {
      setForm((prev) => ({
        ...prev,
        IDProcesso: prev.IDProcesso || String(processes[0].IDProcesso),
      }));
    }
  }, [show, participantClientId, allClientsSelected, operatorAccessProcesses, setForm]);

  useEffect(() => {
    if (!show || lockFullEditFields || !form.IDAgendaGrupo) return;

    const allowedGroupIds = new Set(
      (selectedAgendaGrupo?.operadores || []).map(Number).filter(Boolean),
    );
    const isAllowed = (IDOperador) =>
      allowedGroupIds.has(Number(IDOperador));

    setForm((prev) => {
      const participantes = prev.participantes.filter(isAllowed);
      const responsavelId = Number(prev.IDOperadorResponsavel);
      const responsavelPermitido = isAllowed(responsavelId);
      const proximoResponsavel = responsavelPermitido
        ? prev.IDOperadorResponsavel
        : "";

      if (
        String(proximoResponsavel) === String(prev.IDOperadorResponsavel) &&
        participantes.length === prev.participantes.length
      ) {
        return prev;
      }

      return {
        ...prev,
        IDOperadorResponsavel: proximoResponsavel,
        participantes,
      };
    });
  }, [
    show,
    lockFullEditFields,
    form.IDAgendaGrupo,
    selectedAgendaGrupo,
    setForm,
  ]);

  if (!show) return null;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateAgendaGroup = (IDAgendaGrupo) => {
    const nextGroup = agendaGrupos.find(
      (grupo) => Number(grupo.IDAgendaGrupo) === Number(IDAgendaGrupo),
    );
    const nextGroupOperatorIds = new Set(
      (nextGroup?.operadores || []).map(Number).filter(Boolean),
    );

    setForm((prev) => {
      if (!IDAgendaGrupo) {
        return {
          ...prev,
          IDAgendaGrupo: "",
          IDOperadorResponsavel: isAdmin
            ? prev.IDOperadorResponsavel || Number(currentOperatorId)
            : Number(currentOperatorId),
          participantes: isAdmin ? prev.participantes : [],
        };
      }

      const responsavelId = Number(prev.IDOperadorResponsavel);
      const responsavelPermitido = nextGroupOperatorIds.has(responsavelId);
      return {
        ...prev,
        IDAgendaGrupo,
        IDOperadorResponsavel: responsavelPermitido
          ? prev.IDOperadorResponsavel
          : "",
        participantes: prev.participantes.filter((IDOperador) =>
          nextGroupOperatorIds.has(Number(IDOperador)),
        ),
      };
    });
  };
  const toggleParticipant = (id) => {
    const numericId = Number(id);
    const isAdding = !form.participantes.includes(numericId);

    setForm((prev) => ({
      ...prev,
      participantes: prev.participantes.includes(numericId)
        ? prev.participantes.filter((participantId) => participantId !== numericId)
        : [...prev.participantes, numericId],
    }));

    if (isAdding) setParticipantSearch("");
  };
  const addAllVisibleParticipants = () => {
    const visibleIds = filteredParticipants
      .map((op) => Number(op.IDOperador))
      .filter(Boolean);

    if (!visibleIds.length) return;

    setForm((prev) => ({
      ...prev,
      participantes: Array.from(new Set([...prev.participantes, ...visibleIds])),
    }));
  };
  const selectedParticipants = operadores.filter((op) => form.participantes.includes(Number(op.IDOperador)));
  const availableProcesses = operatorAccessProcesses.filter(
    (process) =>
      (!participantClientId || allClientsSelected ||
        Number(getClientId(process)) === Number(participantClientId)) &&
      Number(process.situacao) === 1,
  );
  const currentOperator =
    operadores.find(
      (op) => Number(op.IDOperador) === Number(currentOperatorId),
    ) || { IDOperador: Number(currentOperatorId), nome: "Voce" };
  const responsibleOperators = hasSelectedGroup
    ? operadores.filter((op) => groupOperatorIds.has(Number(op.IDOperador)))
    : isAdmin
      ? operadores
      : [currentOperator];
  const filteredParticipants = operadores.filter((op) => {
    const operatorId = Number(op.IDOperador);
    const belongsToGroup = groupOperatorIds.has(operatorId);
    const matchesRelationship =
      allClientsSelected
        ? belongsToGroup && participantSearch.trim().length >= 2
        : hasSelectedGroup && belongsToGroup;
    const matchesSearch = removeAccents(`${operatorLabel(op)} ${String(op.IDOperador).padStart(6, "0")}`)
      .toLowerCase()
      .includes(removeAccents(participantSearch).toLowerCase());

    return matchesRelationship && matchesSearch;
  });

  return (
    <div className="agenda-modal-backdrop" role="presentation">
      <section className="agenda-modal" role="dialog" aria-modal="true" aria-labelledby="agenda-modal-title">
        <header>
          <div>
            <span>{item ? "Editar item" : "Novo item"}</span>
            <h2 id="agenda-modal-title">{item ? "Atualizar agenda" : "Criar agenda"}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar modal" title="Fechar">
            <X size={20} />
          </button>
        </header>

        <div className="agenda-form-grid">
          <label className="span-2">
            Titulo
            <input value={form.titulo} onChange={(e) => update("titulo", e.target.value)} maxLength={120} autoFocus={!lockFullEditFields} disabled={lockFullEditFields} />
          </label>

          <label>
            Tipo
            <select value={form.tipo} onChange={(e) => update("tipo", e.target.value)} disabled={lockFullEditFields}>
              {tiposDisponiveis.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
            </select>
          </label>

          <label>
            Prioridade
            <select value={form.prioridade} onChange={(e) => update("prioridade", e.target.value)} disabled={lockFullEditFields}>
              {PRIORIDADES.map((prioridade) => <option key={prioridade.value} value={prioridade.value}>{prioridade.label}</option>)}
            </select>
          </label>

          <label>
            Grupo
            <select value={form.IDAgendaGrupo} onChange={(e) => updateAgendaGroup(e.target.value)} disabled={lockFullEditFields}>
              <option value="">{isAdmin ? "Sem grupo" : "Pessoal (somente para mim)"}</option>
              {agendaGrupos.map((grupo) => (
                <option key={grupo.IDAgendaGrupo} value={grupo.IDAgendaGrupo}>
                  {grupo.nome}
                </option>
              ))}
            </select>
          </label>

          <label>
            Responsavel
            <select value={form.IDOperadorResponsavel} onChange={(e) => update("IDOperadorResponsavel", Number(e.target.value))} disabled={(!hasSelectedGroup && !isAdmin) || lockFullEditFields}>
              <option value="">Selecione o responsavel</option>
              {responsibleOperators.map((op) => (
                <option key={op.IDOperador} value={op.IDOperador}>
                  {op.nome}
                </option>
              ))}
            </select>
          </label>

          <label>
            Status
            <select value={form.statusKanban} onChange={(e) => update("statusKanban", e.target.value)}>
              {STATUS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </label>

          <label>
            Inicio
            <input type="datetime-local" value={form.dataInicio} onChange={(e) => update("dataInicio", e.target.value)} required disabled={lockFullEditFields} />
          </label>

          <label>
            Fim
            <input type="datetime-local" value={form.dataFim} onChange={(e) => update("dataFim", e.target.value)} disabled={lockFullEditFields} />
          </label>

          <label>
            Local
            <input value={form.local} onChange={(e) => update("local", e.target.value)} maxLength={120} disabled={lockFullEditFields} />
          </label>

          <label>
            Link da reuniao
            <div className="agenda-input-icon">
              <LinkIcon size={16} />
              <input value={form.linkReuniao} onChange={(e) => update("linkReuniao", e.target.value)} disabled={lockFullEditFields} />
            </div>
          </label>

          {useDescriptionAppendMode ? (
            <>
              <label className="span-2">
                Descricao da tarefa
                <textarea value={form.descricao} onChange={(e) => update("descricao", e.target.value)} rows={4} readOnly={!canUseAdminEditOptions} />
              </label>

              <label className="span-2">
                Nova mensagem
                <textarea value={form.descricaoNova} onChange={(e) => update("descricaoNova", e.target.value)} rows={3} />
              </label>
            </>
          ) : (
            <label className="span-2">
              Descricao
              <textarea value={form.descricao} onChange={(e) => update("descricao", e.target.value)} rows={4} />
            </label>
          )}

          {showClientProcessFields && (
            <div className="span-2 agenda-participant-scope">
              <div className="agenda-participant-scope-fields">
                <label className="agenda-task-field">
                  <span className="agenda-task-field-label">
                    Cliente
                  </span>
                  <select
                    id="agenda-task-client"
                    value={participantClientId}
                    onChange={(e) => {
                      const IDCliente = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        IDCliente,
                        IDProcesso: "",
                        participantes: [],
                      }));
                      setParticipantSearch("");
                    }}
                disabled={loadingParticipantFilter || lockFullEditFields}
                  >
                    {canUseAdminEditOptions && <option value={ALL_CLIENTS_VALUE}>Todos</option>}
                    <option value="">Selecione o cliente</option>
                    {participantClients.filter(getClientId).map((client) => (
                      <option key={getClientId(client)} value={getClientId(client)}>
                        {clientLabel(client)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="agenda-task-field">
                  <span className="agenda-task-field-label">
                    Processo
                  </span>
                  <select
                    id="agenda-task-process"
                    value={participantProcessId}
                    onChange={(e) => {
                      const IDProcesso = e.target.value;
                      const selectedProcess = operatorAccessProcesses.find(
                        (process) =>
                          Number(process.IDProcesso) === Number(IDProcesso),
                      );
                      setForm((prev) => ({
                        ...prev,
                        IDCliente: selectedProcess
                          ? String(getClientId(selectedProcess))
                          : prev.IDCliente,
                        IDProcesso,
                        participantes: [],
                      }));
                      setParticipantSearch("");
                    }}
                disabled={loadingParticipantFilter || allClientsSelected || lockFullEditFields}
                  >
                    <option value="">{allClientsSelected ? "Sem processo" : "Selecione o processo"}</option>
                    {availableProcesses.map((process) => (
                      <option key={process.IDProcesso} value={process.IDProcesso}>
                        {processLabel(process)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}

          <div className="span-2 agenda-participants">
            <div className="agenda-participants-header">
              <div>
                <span>Participantes</span>
                <small>
                  {isPersonalItem && !isAdmin
                    ? "Demandas pessoais ficam visiveis somente para voce."
                    : isCommonOperatorCreatingReminder
                    ? "Selecione cliente e processo para vincular o lembrete."
                    : allClientsSelected
                      ? "Digite o nome do operador e busque."
                    : "Operadores adicionados ficam vinculados automaticamente."}
                </small>
              </div>
              <strong>{selectedParticipants.length} selecionado{selectedParticipants.length === 1 ? "" : "s"}</strong>
            </div>

            <div className="agenda-selected-participants" aria-live="polite">
              {selectedParticipants.length > 0 ? (
                selectedParticipants.map((op) => (
                  <button
                    type="button"
                    key={op.IDOperador}
                    onClick={() => toggleParticipant(op.IDOperador)}
                    disabled={disableParticipantEditing}
                    aria-label={`Remover ${operatorLabel(op)} dos participantes`}
                    title="Remover participante"
                  >
                    {operatorLabel(op)}
                    <X size={14} />
                  </button>
                ))
              ) : (
                <p>Nenhum participante adicional.</p>
              )}
            </div>

            <label className="agenda-participant-search">
              <Search size={16} aria-hidden="true" />
              <input
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                placeholder={
                  isCommonOperatorCreatingReminder
                    ? "Participantes indisponiveis para lembrete"
                    : allClientsSelected
                    ? "Buscar operador do grupo"
                    : hasSelectedGroup
                      ? "Buscar operador do grupo"
                      : "Demanda pessoal"
                }
                disabled={disableParticipantEditing || !hasSelectedGroup || loadingParticipantFilter}
              />
            </label>

            <div className="agenda-participant-list">
              {!disableParticipantEditing && filteredParticipants.length > 0 && (
                <button
                  type="button"
                  className="agenda-participant-all"
                  onClick={addAllVisibleParticipants}
                  disabled={disableParticipantEditing}
                >
                  <span className="agenda-participant-avatar">T</span>
                  <span className="agenda-participant-copy">
                    <strong>Todos</strong>
                    <small>Adicionar todos os operadores listados</small>
                  </span>
                  <span className="agenda-participant-check"><Plus size={16} /></span>
                </button>
              )}
              {!disableParticipantEditing && filteredParticipants.map((op) => {
                const selected = form.participantes.includes(Number(op.IDOperador));
                const disabled = disableParticipantEditing;
                return (
                  <button
                    type="button"
                    key={op.IDOperador}
                    className={selected ? "selected" : ""}
                    onClick={() => toggleParticipant(op.IDOperador)}
                    disabled={disabled}
                    aria-pressed={selected}
                  >
                    <span className="agenda-participant-avatar">{String(operatorLabel(op) || "?").charAt(0).toUpperCase()}</span>
                    <span className="agenda-participant-copy">
                      <strong>{operatorLabel(op)}</strong>
                      <small>{String(op.IDOperador).padStart(6, "0")}</small>
                    </span>
                    <span className="agenda-participant-check">{selected ? <Check size={16} /> : <Plus size={16} />}</span>
                  </button>
                );
              })}
              {(disableParticipantEditing || filteredParticipants.length === 0) && (
                <p>
                  {loadingParticipantFilter
                    ? "Carregando operadores..."
                    : isCommonOperatorCreatingReminder
                      ? "Operador comum nao adiciona participantes ao criar lembrete."
                      : allClientsSelected
                    ? "Nenhum operador do grupo encontrado."
                      : hasSelectedGroup
                      ? "Nenhum operador do grupo encontrado."
                      : "Demandas pessoais nao possuem outros participantes."}
                </p>
              )}
            </div>
          </div>
        </div>

        <footer>
          <button type="button" className="agenda-secondary-button" onClick={onClose}>Cancelar</button>
          <button type="button" className="agenda-primary-button" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 size={18} className="spin" /> : <Check size={18} />}
            Salvar
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function Agenda() {
  const { authReady } = useAuthContext();
  const { IDOperador, nome, operador, permissaoPrincipal } = useOperador();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [agendaGrupos, setAgendaGrupos] = useState([]);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [view, setView] = useState("kanban");
  const [showAllAgenda, setShowAllAgenda] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const loadingAgendaRef = useRef(false);
  const loadedCalendarPeriodsRef = useRef(new Set());
  const loadingCalendarPeriodsRef = useRef(new Set());
  const openedNotificationItemRef = useRef(null);

  const idFormatado = String(IDOperador || "").padStart(6, "0");
  const permissaoAtual = Number(permissaoPrincipal || operador?.permissaoPrincipal || 0);
  const isAdmin = permissaoAtual === 99;
  const isManager = permissaoAtual === 90;
  const canViewAllAgenda = isAdmin && showAllAgenda;

  const operadoresMap = useMemo(() => {
    const map = new Map();
    operadores.forEach((op) => map.set(Number(op.IDOperador), op));
    return map;
  }, [operadores]);

  const normalizeItem = useCallback((item) => {
    const responsavelId = getOperatorId(item, ["IDOperadorResponsavel", "IDResponsavel", "responsavelId", "idResponsavel"]) || IDOperador;
    const criadorId = getOperatorId(item, ["IDOperadorCriador", "IDCriador", "criadoPor", "IDOperador"]) || responsavelId;
    const participants = item.participantes || item.Participantes || item.operadores || [];
    const participantDetails = Array.isArray(participants)
      ? participants.filter((participante) => typeof participante === "object")
      : [];
    const participantIds = Array.isArray(participants)
      ? participants
          .map((p) => Number(p.IDOperador || p.id || p))
          .filter((participantId) => participantId && participantId !== Number(criadorId))
      : [];
    const currentParticipant = participantDetails.find(
      (participante) => Number(participante.IDOperador) === Number(IDOperador)
    );
    const statusParticipanteOperador =
      item.statusParticipanteOperador ||
      item.statusParticipanteAtual ||
      currentParticipant?.statusParticipante;

    return {
      ...item,
      IDAgendaTarefa: getId(item),
      titulo: item.titulo || item.Titulo || item.descricaoTitulo || "Item sem titulo",
      descricao: item.descricao || item.Descricao || "",
      tipo: normalizeTipo(item.tipo || item.Tipo),
      IDAgendaGrupo: item.IDAgendaGrupo || item.idAgendaGrupo || "",
      agendaGrupoNome: item.agendaGrupoNome || item.nomeAgendaGrupo || item.grupoNome || "",
      IDCliente: item.IDCliente || item.idCliente || item.Cliente?.IDCliente || "",
      IDProcesso: item.IDProcesso || item.idProcesso || item.Processo?.IDProcesso || "",
      clienteNome: itemClientLabel(item),
      clienteMunicipio: itemMunicipio(item),
      processoNome: itemProcessLabel(item),
      IDOperadorResponsavel: Number(responsavelId),
      IDOperadorCriador: Number(criadorId),
      participantes: participantIds,
      participanteDetalhes: participantDetails.filter(
        (participante) => Number(participante.IDOperador) !== Number(criadorId)
      ),
      dataInicio: item.dataInicio || item.DataInicio || item.inicio || item.dtInicio,
      dataFim: item.dataFim || item.DataFim || item.fim || item.dtFim,
      prioridade: removeAccents(item.prioridade || item.Prioridade || "media").toLowerCase(),
      local: item.local || item.Local || "",
      linkReuniao: item.linkReuniao || item.LinkReuniao || item.link || "",
      statusKanban: normalizeStatus(item.statusKanban || item.status || item.Status),
      statusAgenda: normalizeApproval(item.statusAgenda || item.aprovacao || item.statusAprovacao || item.situacaoAprovacao),
      statusParticipanteOperador: statusParticipanteOperador
        ? normalizeApproval(statusParticipanteOperador)
        : null,
      vinculadoAoOperadorAtual:
        item.vinculadoAoOperadorAtual === true ||
        Number(currentParticipant?.situacao) === 1,
    };
  }, [IDOperador]);

  const normalizeAgendaResponses = useCallback((agendaResponse, agendaOperadorResponse) => {
    const operatorItemsById = new Map(
      parseList(agendaOperadorResponse).map((item) => {
        const normalized = normalizeItem(item);
        return [Number(getId(normalized)), normalized];
      })
    );
    const linkedIds = new Set(operatorItemsById.keys());

    return parseList(agendaResponse).map((item) => {
      const normalized = normalizeItem(item);
      const operatorItem = operatorItemsById.get(Number(getId(normalized)));

      return {
        ...normalized,
        convitePendenteParaMim: false,
        vinculadoAoOperadorAtual: linkedIds.has(Number(getId(normalized))),
        statusParticipanteOperador:
          operatorItem?.statusParticipanteOperador ??
          normalized.statusParticipanteOperador,
      };
    });
  }, [normalizeItem]);

  useEffect(() => {
    if (!operador && !authReady) navigate("/");
  }, [operador, authReady, navigate]);

  const loadOperadores = useCallback(async () => {
    const response = await api.get("/operadores");
    const lista = parseList(response);
    setOperadores(lista);
    return lista;
  }, []);

  const loadAgendaGrupos = useCallback(async () => {
    if (!IDOperador) return [];
    const response = isAdmin
      ? await api.get("/agenda-grupos")
      : await api.get(`/agenda-grupos/operador/${IDOperador}`);
    const lista = parseList(response);
    setAgendaGrupos(lista);
    return lista;
  }, [IDOperador, isAdmin]);

  const loadAccessibleClients = useCallback(async () => {
    if (!IDOperador) return [];

    try {
      const response = await api.get("/clientes");
      const lista = parseList(response).filter(getClientId);
      setClientes(lista);
      return lista;
    } catch (err) {
      console.error(err);
      setClientes([]);
      return [];
    }
  }, [IDOperador]);

  const loadAniversariantes = useCallback(async () => {
    try {
      const response = await api.get("/agenda/aniversarios");
      const lista = parseList(response);
      setAniversariantes(lista);
      return lista;
    } catch (err) {
      console.error("Nao foi possivel carregar os aniversariantes.", err);
      setAniversariantes([]);
      return [];
    }
  }, []);

  const loadAgenda = useCallback(async ({ silent = false } = {}) => {
    if (!IDOperador || loadingAgendaRef.current) return;

    loadingAgendaRef.current = true;
    if (!silent) {
      setLoading(true);
      setError("");
    }

    try {
      const listParams = { page: 1, limit: 9999 };
      const agendaRequest = canViewAllAgenda
        ? api.get("/agenda", { params: { ...listParams, visualizarTudoAgenda: 1 } })
        : api.get(`/agenda/operador/${IDOperador}`, { params: listParams });
      const agendaOperadorRequest = canViewAllAgenda
        ? api.get(`/agenda/operador/${IDOperador}`, { params: { ...listParams, visualizarTudoAgenda: 1 } })
        : agendaRequest;
      void loadAniversariantes();
      const [agendaResponse, agendaOperadorResponse, , , gruposResponse] = await Promise.all([
        agendaRequest,
        agendaOperadorRequest,
        loadOperadores(),
        loadAccessibleClients(),
        loadAgendaGrupos(),
      ]);
      if (!isAdmin && !canViewAllAgenda && gruposResponse.length === 0) {
        setItems([]);
        setError("Seu operador ainda nao faz parte de um grupo da agenda.");
        return;
      }
      setItems(normalizeAgendaResponses(agendaResponse, agendaOperadorResponse));
    } catch (err) {
      console.error(err);
      if (!silent) {
        setError("Nao foi possivel carregar a agenda.");
        Swal.fire("Erro", "Nao foi possivel carregar a agenda.", "error");
      }
    } finally {
      loadingAgendaRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [IDOperador, isAdmin, canViewAllAgenda, loadOperadores, loadAccessibleClients, loadAgendaGrupos, loadAniversariantes, normalizeAgendaResponses]);

  const loadCalendarPeriod = useCallback(async ({ periodoInicio, periodoFim }) => {
    if (!IDOperador || !periodoInicio || !periodoFim) return;

    const scope = canViewAllAgenda ? "todos" : "grupos";
    const periodKey = `${scope}:${periodoInicio}:${periodoFim}`;
    if (
      loadedCalendarPeriodsRef.current.has(periodKey) ||
      loadingCalendarPeriodsRef.current.has(periodKey)
    ) return;

    loadingCalendarPeriodsRef.current.add(periodKey);

    try {
      const listParams = { page: 1, limit: 9999, periodoInicio, periodoFim };
      const agendaRequest = canViewAllAgenda
        ? api.get("/agenda", { params: { ...listParams, visualizarTudoAgenda: 1 } })
        : api.get(`/agenda/operador/${IDOperador}`, { params: listParams });
      const agendaOperadorRequest = canViewAllAgenda
        ? api.get(`/agenda/operador/${IDOperador}`, { params: { ...listParams, visualizarTudoAgenda: 1 } })
        : agendaRequest;
      const [agendaResponse, agendaOperadorResponse] = await Promise.all([
        agendaRequest,
        agendaOperadorRequest,
      ]);
      const periodItems = normalizeAgendaResponses(agendaResponse, agendaOperadorResponse);

      setItems((currentItems) => {
        const itemsById = new Map(currentItems.map((item) => [Number(getId(item)), item]));
        periodItems.forEach((item) => itemsById.set(Number(getId(item)), item));
        return Array.from(itemsById.values());
      });
      loadedCalendarPeriodsRef.current.add(periodKey);
    } catch (err) {
      console.error("Nao foi possivel carregar o periodo do calendario.", err);
    } finally {
      loadingCalendarPeriodsRef.current.delete(periodKey);
    }
  }, [IDOperador, canViewAllAgenda, normalizeAgendaResponses]);

  useEffect(() => {
    loadedCalendarPeriodsRef.current.clear();
    loadingCalendarPeriodsRef.current.clear();
  }, [IDOperador, canViewAllAgenda]);

  useEffect(() => {
    if (IDOperador) loadAgenda();
  }, [IDOperador, loadAgenda]);

  useEffect(() => {
    if (!IDOperador) return undefined;

    const refreshSilently = () => {
      if (document.visibilityState === "visible") {
        loadAgenda({ silent: true });
      }
    };

    const intervalId = window.setInterval(refreshSilently, 30000);
    document.addEventListener("visibilitychange", refreshSilently);
    window.addEventListener("focus", refreshSilently);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshSilently);
      window.removeEventListener("focus", refreshSilently);
    };
  }, [IDOperador, loadAgenda]);

  const mergedItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(getId(item), item));
    return Array.from(map.values());
  }, [items]);

  const filteredItems = useMemo(() => {
    const termo = removeAccents(filters.search).toLowerCase();
    return mergedItems.filter((item) => {
      const responsavel = getOperatorName(operadoresMap, item.IDOperadorResponsavel, item.responsavelNome || item.responsavel);
      const haystack = removeAccents(`${item.titulo} ${item.local} ${responsavel}`).toLowerCase();
      const matchesSearch = !termo || haystack.includes(termo);
      const matchesStatus = !filters.status || normalizeStatus(item.statusKanban) === filters.status;
      const matchesPriority = !filters.prioridade || item.prioridade === filters.prioridade;
      const matchesOperator = !filters.operador || Number(item.IDOperadorResponsavel) === Number(filters.operador);
      const matchesClient = !filters.cliente || Number(item.IDCliente) === Number(filters.cliente);
      const prazo = item.dataFim || item.dataInicio ? new Date(item.dataFim || item.dataInicio) : null;
      const matchesStart = !filters.inicio || !prazo || prazo >= new Date(`${filters.inicio}T00:00:00`);
      const matchesEnd = !filters.fim || !prazo || prazo <= new Date(`${filters.fim}T23:59:59`);
      const isMine = Number(item.IDOperadorCriador) === Number(IDOperador);
      const assignedToMe = Number(item.IDOperadorResponsavel) === Number(IDOperador);
      const participates = item.participantes?.includes(Number(IDOperador));
      const linkedToMe = item.vinculadoAoOperadorAtual === true;
      const pending = item.convitePendenteParaMim === true;

      const matchesScope =
        filters.scope === "todos" ||
        filters.scope === "grupo" ||
        (filters.scope === "meus" && (isMine || assignedToMe || linkedToMe || pending)) ||
        (filters.scope === "atribuidos" && (assignedToMe || participates || linkedToMe || pending));

      return matchesSearch && matchesStatus && matchesPriority && matchesOperator && matchesClient && matchesStart && matchesEnd && matchesScope;
    }).sort((a, b) => prazoDateValue(a) - prazoDateValue(b));
  }, [mergedItems, filters, operadoresMap, IDOperador]);

  const boardItems = useMemo(
    () => filteredItems.filter((item) => normalizeTipo(item.tipo) === "tarefa"),
    [filteredItems]
  );
  const calendarItems = useMemo(
    () => filteredItems,
    [filteredItems]
  );
  const birthdayItems = useMemo(() => {
    const termo = removeAccents(filters.search).toLowerCase();

    return aniversariantes
      .map((item) => {
        const next = getNextBirthday(item);
        return {
          ...item,
          proximoAniversario: next.date,
          diasRestantes: next.days,
        };
      })
      .filter((item) => {
        const grupos = (item.grupos || []).map((grupo) => grupo.nome).join(" ");
        return (
          item.proximoAniversario &&
          (!termo || removeAccents(`${item.nome} ${grupos}`).toLowerCase().includes(termo))
        );
      })
      .sort((a, b) => a.proximoAniversario - b.proximoAniversario || String(a.nome).localeCompare(String(b.nome), "pt-BR"));
  }, [aniversariantes, filters.search]);
  const visibleItems = view === "kanban"
    ? boardItems
    : view === "aniversarios"
      ? birthdayItems
      : calendarItems;

  const openNew = () => {
    if (!isAdmin && agendaGrupos.length === 0) {
      Swal.fire("Agenda indisponivel", "Seu operador ainda nao faz parte de um grupo da agenda.", "warning");
      return;
    }

    setEditingItem(null);
    setForm({
      ...emptyForm,
      tipo: emptyForm.tipo,
      descricaoNova: "",
      IDOperadorResponsavel: Number(IDOperador),
      IDAgendaGrupo: "",
      IDCliente: "",
      IDProcesso: "",
      participantes: [],
    });
    setModalOpen(true);
  };

  const canChangeItemStatus = useCallback(
    (item) => {
      if (isAdmin || (isManager && normalizeTipo(item?.tipo) === "tarefa")) {
        return true;
      }

      return (
        !isPendingForCurrentOperator(item) &&
        !isRejectedForCurrentOperator(item) &&
        (canManageAgendaItem(item, isAdmin, IDOperador) ||
          canEditAcceptedTaskLimited(item, isAdmin, isManager, IDOperador))
      );
    },
    [IDOperador, isAdmin, isManager],
  );

  const canManageItem = useCallback(
    (item) => canManageAgendaItem(item, isAdmin, IDOperador),
    [IDOperador, isAdmin],
  );

  const isLimitedTaskEdit = useCallback(
    (item) => canEditAcceptedTaskLimited(item, isAdmin, isManager, IDOperador),
    [IDOperador, isAdmin, isManager],
  );

  const canBypassInvitationStatus = useCallback(
    () => isAdmin || isManager,
    [isAdmin, isManager],
  );

  const openDetails = async (item) => {
    setDetailItem(item);
    setDetailLoading(true);

    try {
      const response = await api.get(`/agenda/${getId(item)}`);
      setDetailItem(normalizeItem(response.data));
    } catch (err) {
      console.error(err);
      Swal.fire("Erro", "Nao foi possivel carregar os detalhes do item.", "error");
      setDetailItem(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const itemId = Number(searchParams.get("item"));
    if (
      !itemId ||
      openedNotificationItemRef.current === itemId ||
      !IDOperador
    ) {
      return;
    }

    openedNotificationItemRef.current = itemId;
    setDetailLoading(true);

    api
      .get(`/agenda/${itemId}`)
      .then((response) => {
        setDetailItem(normalizeItem(response.data));
        setSearchParams({}, { replace: true });
      })
      .catch((err) => {
        console.error(err);
        openedNotificationItemRef.current = null;
        Swal.fire(
          "Erro",
          "Nao foi possivel abrir o compromisso selecionado.",
          "error",
        );
      })
      .finally(() => setDetailLoading(false));
  }, [IDOperador, normalizeItem, searchParams, setSearchParams]);

  const openEdit = async (item) => {
    if (!canBypassInvitationStatus(item) && isPendingForCurrentOperator(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }
    if (!canBypassInvitationStatus(item) && isRejectedForCurrentOperator(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }
    if (
      !canFullyEditAgendaItem(item, isAdmin, isManager, IDOperador) &&
      !isLimitedTaskEdit(item)
    ) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }

    let itemDetalhado = item;

    try {
      const response = await api.get(`/agenda/${getId(item)}`);
      itemDetalhado = normalizeItem(response.data);
    } catch (err) {
      console.error(err);
      Swal.fire("Aviso", "Nao foi possivel carregar os participantes atualizados. Confira antes de salvar.", "warning");
    }

    setEditingItem(itemDetalhado);
    setDetailItem(null);
    setForm({
      titulo: itemDetalhado.titulo || "",
      descricao: itemDetalhado.descricao || "",
      descricaoNova: "",
      tipo: normalizeTipo(itemDetalhado.tipo),
      IDAgendaGrupo: itemDetalhado.IDAgendaGrupo ? String(itemDetalhado.IDAgendaGrupo) : "",
      IDCliente: itemDetalhado.IDCliente ? String(itemDetalhado.IDCliente) : ALL_CLIENTS_VALUE,
      IDProcesso: itemDetalhado.IDProcesso ? String(itemDetalhado.IDProcesso) : "",
      IDOperadorResponsavel: Number(itemDetalhado.IDOperadorResponsavel || IDOperador),
      participantes: itemDetalhado.participantes || [],
      dataInicio: toDatetimeLocal(itemDetalhado.dataInicio),
      dataFim: toDatetimeLocal(itemDetalhado.dataFim),
      prioridade: itemDetalhado.prioridade || "media",
      local: itemDetalhado.local || "",
      linkReuniao: itemDetalhado.linkReuniao || "",
      statusKanban: normalizeStatus(itemDetalhado.statusKanban),
    });
    setModalOpen(true);
  };

  const buildPayload = () => {
    const shouldAppendDescription =
      editingItem && normalizeTipo(form.tipo) === "tarefa" && String(form.descricaoNova || "").trim();
    const descricaoFinal = shouldAppendDescription
      ? appendDescriptionEntry(form.descricao, form.descricaoNova, nome)
      : form.descricao.trim();

    if (editingItem && isLimitedTaskEdit(editingItem)) {
      return {
        descricao: descricaoFinal,
        statusKanban: form.statusKanban,
      };
    }

    return {
      titulo: form.titulo.trim(),
      descricao: descricaoFinal,
      tipo: normalizeTipo(form.tipo),
      IDAgendaGrupo: form.IDAgendaGrupo ? Number(form.IDAgendaGrupo) : null,
      IDCliente: form.IDCliente === ALL_CLIENTS_VALUE || !form.IDCliente ? null : Number(form.IDCliente),
      IDProcesso: form.IDCliente === ALL_CLIENTS_VALUE || !form.IDProcesso ? null : Number(form.IDProcesso),
      IDOperadorResponsavel: Number(form.IDOperadorResponsavel),
      notificarAdmins: !editingItem && !isAdmin ? 1 : undefined,
      participantes: form.participantes.map((IDOperadorParticipante) => {
        const participanteExistente = editingItem?.participanteDetalhes?.find(
          (participante) => Number(participante.IDOperador) === Number(IDOperadorParticipante)
        );

        return {
          IDOperador: Number(IDOperadorParticipante),
          ...(participanteExistente?.statusParticipante
            ? { statusParticipante: participanteExistente.statusParticipante }
            : {}),
        };
      }),
      dataInicio: form.dataInicio || null,
      dataFim: form.dataFim || null,
      prioridade: form.prioridade,
      local: form.local.trim(),
      linkReuniao: form.linkReuniao.trim(),
      statusKanban: form.statusKanban,
    };
  };

  const handleSave = async () => {
    const limitedEdit = editingItem && isLimitedTaskEdit(editingItem);

    if (limitedEdit) {
      setSaving(true);
      try {
        await api.put(`/agenda/${getId(editingItem)}`, buildPayload());
        setModalOpen(false);
        await loadAgenda();
        Swal.fire("Sucesso", "Tarefa atualizada com sucesso.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Erro", "Nao foi possivel salvar a tarefa.", "error");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!form.titulo.trim()) {
      Swal.fire("Atencao", "Informe o titulo do item.", "warning");
      return;
    }
    if (!form.dataInicio) {
      Swal.fire("Atencao", "Informe a data e hora de inicio.", "warning");
      return;
    }
    if (!Number(form.IDOperadorResponsavel)) {
      Swal.fire(
        "Atencao",
        "Selecione um responsavel que faca parte do grupo.",
        "warning",
      );
      return;
    }
    const clienteSelecionado =
      Boolean(form.IDCliente) && form.IDCliente !== ALL_CLIENTS_VALUE;
    const processoSelecionado = Boolean(form.IDProcesso);
    const vinculoClienteProcessoObrigatorio =
      Boolean(form.IDAgendaGrupo) &&
      !(!editingItem && !isAdmin && normalizeTipo(form.tipo) === "lembrete");
    const vinculoClienteProcessoIncompleto =
      clienteSelecionado !== processoSelecionado;

    if (
      (vinculoClienteProcessoObrigatorio &&
        (!clienteSelecionado || !processoSelecionado)) ||
      vinculoClienteProcessoIncompleto
    ) {
      const missingField = !clienteSelecionado ? "cliente" : "processo";
      await Swal.fire(
        "Vinculo obrigatorio",
        `Selecione o ${missingField} da tarefa antes de salvar.`,
        "warning"
      );
      document
        .getElementById(
          !form.IDCliente ? "agenda-task-client" : "agenda-task-process"
        )
        ?.focus();
      return;
    }
    if (form.dataFim && new Date(form.dataFim) < new Date(form.dataInicio)) {
      Swal.fire("Atencao", "A data final nao pode ser anterior ao inicio.", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingItem) {
        await api.put(`/agenda/${getId(editingItem)}`, payload);
      } else {
        await api.post("/agenda", payload);
      }
      setModalOpen(false);
      await loadAgenda();
      Swal.fire("Sucesso", "Agenda salva com sucesso.", "success");
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errors?.default || "Nao foi possivel salvar o item.";
      Swal.fire("Erro", message, "error");
    } finally {
      setSaving(false);
    }
  };

  const runItemAction = async (item, endpoint, successMessage, options = {}) => {
    if (options.requiresAcceptance && isPendingForCurrentOperator(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }
    if (options.requiresStatusPermission && !canChangeItemStatus(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }

    const confirmed = options.confirm ? await confirmAction(options.confirm) : true;
    if (!confirmed) return;

    try {
      if (options.method === "delete") {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }
      await loadAgenda();
      if (options.afterSuccess) {
        await options.afterSuccess(item);
      }
      if (options.refreshNotifications) {
        window.dispatchEvent(new CustomEvent("agenda:notifications-refresh"));
      }
      Swal.fire("Sucesso", successMessage, "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Erro", "Nao foi possivel concluir a acao.", "error");
    }
  };

  const moveToStatus = async (item, statusKanban, showSuccess = true) => {
    if (!canChangeItemStatus(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }

    const updateLocalStatus = (list) =>
      list.map((agendaItem) =>
        Number(getId(agendaItem)) === Number(getId(item))
          ? { ...agendaItem, statusKanban }
          : agendaItem
      );

    setItems(updateLocalStatus);

    try {
      await api.put(`/agenda/${getId(item)}/kanban`, { statusKanban });
      await loadAgenda();
      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: "Status atualizado",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
      await loadAgenda();
      Swal.fire("Erro", "Nao foi possivel mover o item.", "error");
    }
  };

  const reorderKanban = async (item, statusKanban, orderedIds) => {
    if (!canChangeItemStatus(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }

    const orderMap = new Map(
      orderedIds.map((id, index) => [Number(id), (index + 1) * 100]),
    );
    const updateLocalOrder = (list) =>
      list.map((agendaItem) => {
        const agendaItemId = Number(getId(agendaItem));
        const isDraggedItem = agendaItemId === Number(getId(item));
        const newOrder = orderMap.get(agendaItemId);

        if (!isDraggedItem && newOrder === undefined) return agendaItem;
        return {
          ...agendaItem,
          ...(isDraggedItem ? { statusKanban } : {}),
          ...(newOrder !== undefined ? { ordemKanban: newOrder } : {}),
        };
      });

    setItems(updateLocalOrder);

    try {
      await api.put(`/agenda/${getId(item)}/kanban`, {
        statusKanban,
        orderedIds,
      });
    } catch (err) {
      console.error(err);
      await loadAgenda();
      Swal.fire("Erro", "Nao foi possivel alterar a ordem dos cards.", "error");
    }
  };

  const handleMove = async (item) => {
    if (!canChangeItemStatus(item)) {
      Swal.fire("Sem permissao", "", "warning");
      return;
    }

    const currentIndex = STATUS.findIndex((status) => status.value === normalizeStatus(item.statusKanban));
    const next = STATUS[Math.min(currentIndex + 1, STATUS.length - 1)] || STATUS[0];

    const { value } = await Swal.fire({
      title: "Mover status",
      input: "select",
      inputOptions: STATUS.reduce((acc, status) => ({ ...acc, [status.value]: status.label }), {}),
      inputValue: next.value,
      showCancelButton: true,
      confirmButtonText: "Mover",
      cancelButtonText: "Voltar",
    });

    if (value) await moveToStatus(item, value);
  };

  const actions = {
    onEdit: openEdit,
    onOpenDetails: openDetails,
    onMove: handleMove,
    onMoveToStatus: moveToStatus,
    onReorder: reorderKanban,
    onCanChangeStatus: canChangeItemStatus,
    onCanManageItem: canManageItem,
    onCanBypassInvitationStatus: canBypassInvitationStatus,
    onCancel: (item) => runItemAction(item, `/agenda/${getId(item)}/cancelar`, "Item cancelado.", {
      requiresAcceptance: true,
      requiresStatusPermission: true,
      confirm: { title: "Cancelar item?", text: item.titulo, confirmButtonText: "Cancelar", icon: "warning" },
    }),
    onDelete: isAdmin
      ? (item) => runItemAction(item, `/agenda/${getId(item)}`, "Item excluido.", {
          confirm: { title: "Excluir item?", text: item.titulo, confirmButtonText: "Excluir", icon: "warning" },
          method: "delete",
        })
      : null,
    onConclude: (item) => runItemAction(item, `/agenda/${getId(item)}/concluir`, "Item concluido.", {
      requiresAcceptance: true,
      requiresStatusPermission: true,
    }),
  };

  return (
    <div className="menu-container-menu">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell agenda-page">
          <MenuSuperior userId={idFormatado} userName={nome} />

          <section className="agenda-header">
            <div>
              <span className="page-eyebrow"><CalendarCheck2 size={16} /> Agenda</span>
              <h1>Agenda</h1>
            </div>
            <div className="agenda-summary">
              <div><strong>{visibleItems.length}</strong><span>itens visiveis</span></div>
              <div><strong>{agendaGrupos.length}</strong><span>grupos</span></div>
              <div>
                <strong>
                  {view === "aniversarios"
                    ? birthdayItems.filter((item) => item.diasRestantes === 0).length
                    : boardItems.filter((item) => normalizeStatus(item.statusKanban) === "andamento").length}
                </strong>
                <span>{view === "aniversarios" ? "aniversarios hoje" : "em andamento"}</span>
              </div>
            </div>
          </section>

          <AgendaFilters
            filters={filters}
            setFilters={setFilters}
            isAdmin={canViewAllAgenda}
            isRealAdmin={isAdmin}
            operadores={operadores}
            clientes={clientes}
            view={view}
            setView={setView}
            onNew={openNew}
            onRefresh={loadAgenda}
            loading={loading}
            showAllAgenda={showAllAgenda}
            setShowAllAgenda={setShowAllAgenda}
          />

          {loading && (
            <section className="agenda-state">
              <Loader2 className="spin" size={28} />
              <p>Carregando agenda...</p>
            </section>
          )}

          {!loading && error && (
            <section className="agenda-state agenda-state-error">
              <AlertCircle size={28} />
              <p>{error}</p>
              <button type="button" onClick={loadAgenda}>Tentar novamente</button>
            </section>
          )}

          {!loading && !error && view !== "lista" && visibleItems.length === 0 && (
            <section className="agenda-state">
              {view === "aniversarios" ? <Cake size={32} /> : <CalendarCheck2 size={32} />}
              <p>
                {view === "kanban"
                  ? "Nenhuma tarefa encontrada para os filtros atuais."
                  : view === "aniversarios"
                    ? "Nenhum aniversario encontrado para os seus grupos."
                    : "Nenhum compromisso encontrado para os filtros atuais."}
              </p>
              {view !== "aniversarios" && <button type="button" onClick={openNew}>Criar primeiro item</button>}
            </section>
          )}

          {!loading && !error && (view === "lista" || visibleItems.length > 0) && (
            view === "kanban"
              ? <AgendaBoard items={boardItems} operadoresMap={operadoresMap} actions={actions} />
              : view === "aniversarios"
                ? <BirthdayList items={birthdayItems} />
                : <AgendaList
                    items={calendarItems}
                    operadoresMap={operadoresMap}
                    actions={actions}
                    onMonthChange={loadCalendarPeriod}
                  />
          )}
        </div>

        <AgendaModal
          show={modalOpen}
          item={editingItem}
          form={form}
          setForm={setForm}
          operadores={operadores.length ? operadores : [{ IDOperador, nome }]}
          agendaGrupos={agendaGrupos}
          isAdmin={isAdmin}
          isManager={isManager}
          currentOperatorId={IDOperador}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          saving={saving}
        />

        <AgendaDetailModal
          item={detailItem}
          loading={detailLoading}
          operadoresMap={operadoresMap}
          isAdmin={isAdmin}
          canBypassInvitationStatus={isAdmin || isManager}
          onClose={() => setDetailItem(null)}
          onEdit={openEdit}
        />
      </div>
    </div>
  );
}
